const norm = (code) => (code ?? '').trim();

/** Full operation catalog + saved grants → flat list for tree builder. */
export function mergeMenuAccessItems(allOperations, rolePayload) {
  const grantedSet = new Set();
  const grantedCodes = rolePayload?.grantedOperationCodes ?? rolePayload?.GrantedOperationCodes;
  if (grantedCodes?.length) {
    grantedCodes.forEach((c) => grantedSet.add(norm(c)));
  } else {
    const roleItems = rolePayload?.items ?? rolePayload?.Items ?? [];
    roleItems.forEach((i) => {
      const granted = i.granted ?? i.Granted;
      if (granted) grantedSet.add(norm(i.operationCode ?? i.OperationCode));
    });
  }

  return (allOperations || []).map((op) => {
    const code = op.operationCode ?? op.OperationCode ?? '';
    const path = op.operationPath ?? op.OperationPath ?? '';
    return {
      operationCode: code,
      operationName: op.operationName ?? op.OperationName ?? '',
      parentOperationCode: op.parentOperationCode ?? op.ParentOperationCode ?? null,
      seqNo: op.seqNo ?? op.SeqNo ?? 0,
      isFolder: path === '#' || !!(op.isFolder ?? op.IsFolder),
      granted: grantedSet.has(norm(code)),
    };
  });
}

export function buildOperationTree(items) {
  if (!items?.length) return [];

  const byCode = new Map();
  items.forEach((item) => {
    byCode.set(norm(item.operationCode), { ...item, children: [] });
  });

  const roots = [];
  items.forEach((item) => {
    const code = norm(item.operationCode);
    const node = byCode.get(code);
    const parentCode = norm(item.parentOperationCode);
    if (!parentCode || parentCode === code || !byCode.has(parentCode)) {
      roots.push(node);
    } else {
      byCode.get(parentCode).children.push(node);
    }
  });

  const sortNodes = (nodes) => {
    nodes.sort(
      (a, b) => (a.seqNo ?? 0) - (b.seqNo ?? 0)
        || String(a.operationCode).localeCompare(String(b.operationCode))
    );
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

function indexByCode(items) {
  return new Map(items.map((i) => [norm(i.operationCode), i]));
}

export function getDescendantCodes(items, rootCode) {
  const childrenByParent = new Map();
  items.forEach((item) => {
    const parent = norm(item.parentOperationCode);
    if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
    childrenByParent.get(parent).push(norm(item.operationCode));
  });

  const result = [];
  const queue = [norm(rootCode)];
  while (queue.length) {
    const code = queue.shift();
    result.push(code);
    (childrenByParent.get(code) || []).forEach((child) => queue.push(child));
  }
  return result;
}

export function getAncestorCodes(items, code) {
  const byCode = indexByCode(items);
  const ancestors = [];
  let parent = norm(byCode.get(norm(code))?.parentOperationCode);
  while (parent && byCode.has(parent)) {
    ancestors.push(parent);
    parent = norm(byCode.get(parent)?.parentOperationCode);
  }
  return ancestors;
}
